/**
 * AIActionCenter — CRM-AI-ACTION-02 (extends CRM-AI-ACT-01)
 *
 * Every supported recommendation type is now executable through
 * its corresponding existing CRM business function.
 *
 * Action dispatch map:
 *   TASK      → batch-create (existing) + AddTaskModal
 *   CALL      → LogCallModal (log completed) OR FollowUpModal (schedule call)
 *   MEETING   → AddMeetingModal
 *   FOLLOW_UP → FollowUpModal
 *   NOTE      → AddNoteModal
 *   MESSAGE   → SendMessageModal
 *   EVENT_INTENT_VALIDATION → ValidateIntentModal (not yet supported via rule)
 *   QUOTE / EVENT_PLANNING  → not yet supported via rule
 *
 * Design principles:
 * - AI recommends. Human confirms. Nothing is created without explicit approval.
 * - All canonical records are created by existing modals — no duplicate engines.
 * - Recommendations are marked ACTIONED only after modal confirms.
 * - TASK, CALL, MEETING, FOLLOW_UP: WORKING. Others: NOT_YET_SUPPORTED.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Sparkles, RefreshCw, ChevronDown, ChevronUp, X, Loader2,
  CheckSquare, Square, AlertCircle, Clock, Calendar, User,
  ClipboardList, PhoneCall, MessageSquare, Send, BellRing,
  FileText, CheckCircle2, Zap, Minus, CheckCheck, Info,
  AlertTriangle, Lock, Phone, CalendarPlus, StickyNote,
} from "lucide-react";
import { toast } from "sonner";
import { recommendationApi, hasCapability } from "../../services/api";

// ── Priority config ────────────────────────────────────────────────────────────
const PRIORITY_CFG = {
  CRITICAL: { color: "text-red-500",   bg: "bg-red-500/10 border-red-500/20",    dot: "bg-red-500" },
  HIGH:     { color: "text-amber-500", bg: "bg-amber-500/10 border-amber-500/20", dot: "bg-amber-500" },
  MEDIUM:   { color: "text-sky-500",   bg: "bg-sky-500/10 border-sky-500/20",     dot: "bg-sky-500" },
  LOW:      { color: "text-slate-400", bg: "bg-slate-500/5 border-slate-500/10",  dot: "bg-slate-400" },
};

const ACTION_ICON = {
  TASK:      ClipboardList,
  CALL:      PhoneCall,
  MEETING:   Calendar,
  MESSAGE:   MessageSquare,
  FOLLOW_UP: BellRing,
  NOTE:      FileText,
};

const ACTION_LABEL = {
  TASK:      "Task",
  CALL:      "Call",
  MEETING:   "Meeting",
  MESSAGE:   "Message",
  FOLLOW_UP: "Follow-up",
  NOTE:      "Note",
};

// ── Intent Lifecycle Badge ─────────────────────────────────────────────────────
const FIELD_VERIF_CFG = {
  UNVERIFIED: { label: "Unverified",   bg: "bg-slate-500/10 text-slate-400 border-slate-500/20" },
  PARTIAL:    { label: "Partial",      bg: "bg-amber-500/10 text-amber-500 border-amber-500/20" },
  VERIFIED:   { label: "Verified",     bg: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
};
const PLANNER_CFG = {
  NOT_REVIEWED:      { label: "Not Reviewed",     bg: "bg-slate-500/10 text-slate-400 border-slate-500/20" },
  IN_REVIEW:         { label: "In Review",        bg: "bg-amber-500/10 text-amber-500 border-amber-500/20" },
  APPROVED:          { label: "Approved",         bg: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  CHANGES_REQUIRED:  { label: "Changes Required", bg: "bg-red-500/10 text-red-400 border-red-500/20" },
};
const SALES_QUAL_CFG = {
  NOT_QUALIFIED: { label: "Not Qualified", bg: "bg-slate-500/10 text-slate-400 border-slate-500/20" },
  IN_PROGRESS:   { label: "In Progress",  bg: "bg-sky-500/10 text-sky-400 border-sky-500/20" },
  QUALIFIED:     { label: "Qualified",    bg: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  DISQUALIFIED:  { label: "Disqualified", bg: "bg-red-500/10 text-red-400 border-red-500/20" },
};

export function IntentLifecycleBadges({ lifecycle, leadId, onUpdate }) {
  const fv = FIELD_VERIF_CFG[lifecycle?.field_verification] || FIELD_VERIF_CFG.UNVERIFIED;
  const pr = PLANNER_CFG[lifecycle?.planner_review] || PLANNER_CFG.NOT_REVIEWED;
  const sq = SALES_QUAL_CFG[lifecycle?.sales_qualification] || SALES_QUAL_CFG.NOT_QUALIFIED;

  return (
    <div className="flex flex-wrap gap-1.5 items-center" data-testid="intent-lifecycle-badges">
      <span className="text-[10px] text-[#706E6B] font-medium uppercase tracking-wide mr-0.5">Intent:</span>
      <span className={`text-[10px] font-medium px-2 py-0.5 rounded border ${fv.bg}`} title="Field Verification">
        Fields: {fv.label}
      </span>
      <span className={`text-[10px] font-medium px-2 py-0.5 rounded border ${pr.bg}`} title="Planner Review">
        Review: {pr.label}
      </span>
      <span className={`text-[10px] font-medium px-2 py-0.5 rounded border ${sq.bg}`} title="Sales Qualification">
        Sales: {sq.label}
      </span>
    </div>
  );
}

// ── Non-TASK dispatch action buttons ──────────────────────────────────────────
function NonTaskActionButtons({ rec, canAct, dispatching, onDispatch }) {
  if (!canAct) {
    return (
      <div className="flex items-center gap-1 text-[10px] text-[#B0AEAC] mt-1.5" data-testid={`rec-perm-note-${rec.rule_id}`}>
        <Lock className="w-3 h-3 flex-shrink-0" />
        Requires CRM activity permissions
      </div>
    );
  }

  const btnBase = "flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded border transition-all disabled:opacity-60";
  const primaryBtn = `${btnBase} bg-[#EEF4FF] border-[#0176D3]/30 text-[#0176D3] hover:bg-[#dbeafe]`;
  const secondaryBtn = `${btnBase} bg-[#F3F3F3] border-[#E5E5E5] text-[#444444] hover:border-[#C9C9C9]`;
  const isDispatching = dispatching === rec.rec_id;

  if (rec.action_type === "CALL") {
    return (
      <div className="flex flex-wrap gap-1.5 mt-2 pt-2 border-t border-[#F3F3F3]" data-testid={`rec-call-actions-${rec.rule_id}`}>
        <button
          className={primaryBtn}
          disabled={isDispatching}
          data-testid={`rec-log-call-btn-${rec.rule_id}`}
          onClick={() => onDispatch(rec, "LOG_CALL")}
        >
          {isDispatching ? <Loader2 className="w-3 h-3 animate-spin" /> : <PhoneCall className="w-3 h-3" />}
          Log Completed Call
        </button>
        <button
          className={secondaryBtn}
          disabled={isDispatching}
          data-testid={`rec-schedule-call-btn-${rec.rule_id}`}
          onClick={() => onDispatch(rec, "SCHEDULE_CALL")}
        >
          <Clock className="w-3 h-3" />
          Schedule Call
        </button>
      </div>
    );
  }

  if (rec.action_type === "MEETING") {
    return (
      <div className="flex mt-2 pt-2 border-t border-[#F3F3F3]" data-testid={`rec-meeting-actions-${rec.rule_id}`}>
        <button
          className={primaryBtn}
          disabled={isDispatching}
          data-testid={`rec-schedule-meeting-btn-${rec.rule_id}`}
          onClick={() => onDispatch(rec, "SCHEDULE_MEETING")}
        >
          {isDispatching ? <Loader2 className="w-3 h-3 animate-spin" /> : <CalendarPlus className="w-3 h-3" />}
          Schedule Meeting
        </button>
      </div>
    );
  }

  if (rec.action_type === "FOLLOW_UP") {
    return (
      <div className="flex mt-2 pt-2 border-t border-[#F3F3F3]" data-testid={`rec-followup-actions-${rec.rule_id}`}>
        <button
          className={primaryBtn}
          disabled={isDispatching}
          data-testid={`rec-schedule-followup-btn-${rec.rule_id}`}
          onClick={() => onDispatch(rec, "SCHEDULE_FOLLOW_UP")}
        >
          {isDispatching ? <Loader2 className="w-3 h-3 animate-spin" /> : <BellRing className="w-3 h-3" />}
          Schedule Follow-up
        </button>
      </div>
    );
  }

  if (rec.action_type === "NOTE") {
    return (
      <div className="flex mt-2 pt-2 border-t border-[#F3F3F3]" data-testid={`rec-note-actions-${rec.rule_id}`}>
        <button
          className={primaryBtn}
          disabled={isDispatching}
          data-testid={`rec-save-note-btn-${rec.rule_id}`}
          onClick={() => onDispatch(rec, "SAVE_NOTE")}
        >
          {isDispatching ? <Loader2 className="w-3 h-3 animate-spin" /> : <StickyNote className="w-3 h-3" />}
          Save Note
        </button>
      </div>
    );
  }

  if (rec.action_type === "MESSAGE") {
    return (
      <div className="flex mt-2 pt-2 border-t border-[#F3F3F3]" data-testid={`rec-message-actions-${rec.rule_id}`}>
        <button
          className={primaryBtn}
          disabled={isDispatching}
          data-testid={`rec-open-composer-btn-${rec.rule_id}`}
          onClick={() => onDispatch(rec, "OPEN_COMPOSER")}
        >
          {isDispatching ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
          Open Composer
        </button>
      </div>
    );
  }

  // Unsupported action type — NOT_YET_SUPPORTED
  return (
    <div className="flex items-center gap-1 text-[10px] text-[#B0AEAC] mt-1.5 pt-2 border-t border-[#F3F3F3]"
      data-testid={`rec-unsupported-${rec.rule_id}`}>
      <Info className="w-3 h-3 flex-shrink-0" />
      Action via quick buttons below
    </div>
  );
}

// ── Single Recommendation Card ─────────────────────────────────────────────────

/**
 * Parse urgency evidence fields from a source_evidence array.
 * Returns { eventDate, daysRemaining, threshold } for urgency recs.
 */
function parseUrgencyEvidence(sourceEvidence = []) {
  const ev = { eventDate: null, daysRemaining: null, threshold: null };
  for (const item of (sourceEvidence || [])) {
    const [k, v] = item.split("=");
    if (k === "event_date") ev.eventDate = v;
    else if (k === "days_remaining") ev.daysRemaining = parseInt(v, 10);
    else if (k === "threshold") ev.threshold = v;
  }
  return ev;
}

function RecommendationCard({ rec, selected, onToggle, onEdit, onDismiss, canAct, dispatching, onDispatch }) {
  const [expanded, setExpanded]       = useState(rec.priority === "CRITICAL");
  const [editedTitle, setEditedTitle] = useState(rec.title);
  const [editedDesc, setEditedDesc]   = useState(rec.editable_description || "");
  const [editedDue, setEditedDue]     = useState(rec.suggested_due_date || "");
  const [priority, setPriority]       = useState(rec.priority);
  // null | "MENU" | { action:"DISMISS"|"DISMISS_NOT_APPLICABLE", reason:"", error:null }
  const [dismissFlow, setDismissFlow] = useState(null);
  const [dismissing, setDismissing]   = useState(false);

  const pcfg = PRIORITY_CFG[priority] || PRIORITY_CFG.MEDIUM;
  const ActionIcon = ACTION_ICON[rec.action_type] || ClipboardList;
  const isExisting = rec.review_status === "EXISTING_TASK";
  const isTaskType = rec.action_type === "TASK";
  const isUrgency  = (rec.rule_id || "").startsWith("R_URGENCY");
  const urgency    = isUrgency ? parseUrgencyEvidence(rec.source_evidence) : null;

  // Propagate edits upward on change
  useEffect(() => {
    onEdit(rec.rec_id, { title: editedTitle, due_date: editedDue, description: editedDesc, priority });
  }, [editedTitle, editedDue, editedDesc, priority]); // eslint-disable-line

  // ── Dismiss flow handlers ──────────────────────────────────────────────────
  const openMenu = (e) => { e.stopPropagation(); setDismissFlow("MENU"); };
  const cancel   = ()    => setDismissFlow(null);

  const doSnooze = async (hours) => {
    setDismissing(true);
    try { await onDismiss(rec.rec_id, "SNOOZE", hours, ""); }
    finally { setDismissing(false); setDismissFlow(null); }
  };

  const pickReasonAction = (action) => setDismissFlow({ action, reason: "", error: null });

  const confirmReason = async () => {
    const reason = (dismissFlow?.reason || "").trim();
    if (!reason) {
      setDismissFlow(f => ({ ...f, error: "A reason is required." }));
      return;
    }
    setDismissing(true);
    try { await onDismiss(rec.rec_id, dismissFlow.action, 48, reason); }
    finally { setDismissing(false); setDismissFlow(null); }
  };

  return (
    <div
      className={`border rounded-lg overflow-hidden transition-all duration-200 ${
        isExisting
          ? "opacity-60 border-[#E5E5E5]"
          : selected
          ? "border-[#C9A15F]/60 bg-[#C9A15F]/5"
          : "border-[#E5E5E5] hover:border-[#C9A15F]/30"
      }`}
      data-testid={`rec-card-${rec.rule_id}`}
    >
      {/* Card header */}
      <div className="flex items-start gap-2 p-3">
        {/* Checkbox (TASK only) */}
        <div className="mt-0.5 flex-shrink-0">
          {isTaskType && !isExisting ? (
            <button onClick={() => onToggle(rec.rec_id)} className="text-[#706E6B] hover:text-[#C9A15F] transition-colors"
              data-testid={`rec-select-${rec.rule_id}`}>
              {selected ? <CheckSquare className="w-4 h-4 text-[#C9A15F]" /> : <Square className="w-4 h-4" />}
            </button>
          ) : (
            <ActionIcon className="w-4 h-4 text-[#706E6B] mt-0.5" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${pcfg.dot}`} />
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${pcfg.bg} ${pcfg.color}`}>
              {ACTION_LABEL[rec.action_type] || rec.action_type}
            </span>
            {/* WF-06: label only when AI was actually invoked */}
            {rec.ai_enhanced ? (
              <span className="text-[10px] flex items-center gap-0.5 text-purple-400 font-medium" data-testid={`rec-ai-label-${rec.rule_id}`}>
                <Sparkles className="w-3 h-3" /> AI-assisted
              </span>
            ) : (
              <span className="text-[10px] flex items-center gap-0.5 text-slate-400 font-medium" data-testid={`rec-system-label-${rec.rule_id}`}>
                <CheckCircle2 className="w-3 h-3" /> System rules
              </span>
            )}
            {isExisting && (
              <span className="text-[10px] flex items-center gap-0.5 text-amber-500" data-testid={`rec-existing-${rec.rule_id}`}>
                <CheckCircle2 className="w-3 h-3" /> Task exists
              </span>
            )}
          </div>

          {expanded && !isExisting ? (
            <input
              value={editedTitle}
              onChange={e => setEditedTitle(e.target.value)}
              className="mt-1.5 w-full text-sm font-medium text-[#1A1410] bg-transparent border-b border-[#E5E5E5] focus:border-[#C9A15F] outline-none pb-0.5"
              data-testid={`rec-title-input-${rec.rule_id}`}
            />
          ) : (
            <p className="mt-1 text-sm font-medium text-[#1A1410] leading-snug">{editedTitle}</p>
          )}

          <p className="mt-1 text-xs text-[#706E6B] leading-relaxed">
            {rec.ai_explanation || rec.explanation}
          </p>

          {/* WF: Urgency evidence strip — event date, days remaining, threshold */}
          {isUrgency && urgency?.daysRemaining != null && (
            <div
              className="mt-1.5 flex flex-wrap items-center gap-2 text-[10px] rounded px-2 py-1 bg-red-50 border border-red-100"
              data-testid={`rec-urgency-evidence-${rec.rule_id}`}
            >
              <span className="flex items-center gap-1 text-red-700 font-semibold">
                <AlertTriangle className="w-3 h-3" />
                {urgency.daysRemaining} days remaining
              </span>
              {urgency.eventDate && (
                <span className="text-red-600">Event: {urgency.eventDate}</span>
              )}
              {urgency.threshold && (
                <span className="text-red-500 font-mono text-[9px]">threshold: {urgency.threshold}</span>
              )}
            </div>
          )}

          {!expanded && (
            <p className="mt-1 text-[10px] text-[#706E6B] flex items-center gap-1">
              <Clock className="w-3 h-3" /> {rec.suggested_timing}
            </p>
          )}

          {/* Non-TASK action buttons */}
          {!isTaskType && !isExisting && (
            <NonTaskActionButtons rec={rec} canAct={canAct} dispatching={dispatching} onDispatch={onDispatch} />
          )}
        </div>

        {/* Expand / collapse */}
        <button onClick={() => setExpanded(e => !e)} className="flex-shrink-0 text-[#706E6B] hover:text-[#444444] transition-colors mt-0.5">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Expanded details */}
      {expanded && !isExisting && (
        <div className="px-3 pb-3 border-t border-[#F3F3F3] pt-2.5 space-y-2.5">
          <div>
            <label className="text-[10px] font-semibold text-[#706E6B] uppercase tracking-wide">Description</label>
            <textarea
              value={editedDesc}
              onChange={e => setEditedDesc(e.target.value)}
              rows={3}
              className="mt-1 w-full text-xs text-[#444444] bg-[#F9F9F9] border border-[#E5E5E5] rounded p-2 focus:border-[#C9A15F] outline-none resize-none"
              data-testid={`rec-desc-input-${rec.rule_id}`}
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-[10px] font-semibold text-[#706E6B] uppercase tracking-wide">Due Date</label>
              <input
                type="date"
                value={editedDue}
                onChange={e => setEditedDue(e.target.value)}
                className="mt-1 w-full text-xs text-[#444444] bg-[#F9F9F9] border border-[#E5E5E5] rounded px-2 py-1.5 focus:border-[#C9A15F] outline-none"
                data-testid={`rec-due-input-${rec.rule_id}`}
              />
            </div>
            <div className="flex-1">
              <label className="text-[10px] font-semibold text-[#706E6B] uppercase tracking-wide">Priority</label>
              <select
                value={priority}
                onChange={e => setPriority(e.target.value)}
                className="mt-1 w-full text-xs text-[#444444] bg-[#F9F9F9] border border-[#E5E5E5] rounded px-2 py-1.5 focus:border-[#C9A15F] outline-none"
                data-testid={`rec-priority-select-${rec.rule_id}`}
              >
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
            </div>
          </div>

          {rec.source_evidence?.length > 0 && (
            <p className="text-[10px] text-[#706E6B] flex items-start gap-1">
              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
              Based on: {rec.source_evidence.join(" · ")}
            </p>
          )}

          {/* ── Dismiss flow ─────────────────────────────────────────────── */}
          <div className="pt-1">
            {/* initial */}
            {!dismissFlow && (
              <button onClick={openMenu}
                className="text-[11px] text-[#706E6B] hover:text-[#444444] flex items-center gap-1 transition-colors"
                data-testid={`rec-defer-btn-${rec.rule_id}`}>
                <Minus className="w-3 h-3" /> Defer
              </button>
            )}

            {/* 4-option menu */}
            {dismissFlow === "MENU" && (
              <div className="flex flex-wrap gap-1.5 items-center" data-testid={`rec-dismiss-menu-${rec.rule_id}`}>
                <button onClick={() => doSnooze(24)} disabled={dismissing}
                  className="text-[11px] px-2 py-1 border border-amber-200 rounded text-amber-600 hover:border-amber-400 transition-colors"
                  data-testid={`rec-snooze-24-${rec.rule_id}`}>
                  Snooze 24h
                </button>
                <button onClick={() => doSnooze(48)} disabled={dismissing}
                  className="text-[11px] px-2 py-1 border border-amber-200 rounded text-amber-600 hover:border-amber-400 transition-colors"
                  data-testid={`rec-snooze-48-${rec.rule_id}`}>
                  Snooze 48h
                </button>
                <button onClick={() => pickReasonAction("DISMISS_NOT_APPLICABLE")} disabled={dismissing}
                  className="text-[11px] px-2 py-1 border border-slate-200 rounded text-slate-600 hover:border-slate-400 transition-colors"
                  data-testid={`rec-not-applicable-${rec.rule_id}`}>
                  Not applicable
                </button>
                <button onClick={() => pickReasonAction("DISMISS")} disabled={dismissing}
                  className="text-[11px] px-2 py-1 border border-red-100 rounded text-red-500 hover:border-red-300 transition-colors"
                  data-testid={`rec-dismiss-perm-${rec.rule_id}`}>
                  Dismiss permanently
                </button>
                <button onClick={cancel} className="text-[11px] text-[#706E6B] hover:text-[#444444] px-1">Cancel</button>
              </div>
            )}

            {/* reason capture */}
            {dismissFlow && typeof dismissFlow === "object" && (
              <div className="space-y-2" data-testid={`rec-dismiss-reason-form-${rec.rule_id}`}>
                <p className="text-[11px] text-[#444444] font-medium">
                  {dismissFlow.action === "DISMISS_NOT_APPLICABLE"
                    ? "Why is this recommendation not applicable?"
                    : "Reason for permanent dismissal (required):"}
                </p>
                <textarea
                  value={dismissFlow.reason}
                  onChange={e => setDismissFlow(f => ({ ...f, reason: e.target.value, error: null }))}
                  rows={2}
                  placeholder="Briefly explain why this action is not relevant…"
                  className="w-full text-xs border border-[#E5E5E5] rounded p-2 focus:border-[#C9A15F] outline-none resize-none"
                  data-testid={`rec-dismiss-reason-input-${rec.rule_id}`}
                  autoFocus
                />
                {dismissFlow.error && (
                  <p className="text-[10px] text-red-500" data-testid={`rec-dismiss-reason-error-${rec.rule_id}`}>{dismissFlow.error}</p>
                )}
                <div className="flex gap-2">
                  <button onClick={confirmReason} disabled={dismissing}
                    className="text-[11px] px-3 py-1 bg-[#1A1410] text-white rounded hover:bg-[#2a2018] transition-colors flex items-center gap-1"
                    data-testid={`rec-dismiss-confirm-${rec.rule_id}`}>
                    {dismissing ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    Confirm
                  </button>
                  <button onClick={cancel} className="text-[11px] text-[#706E6B] hover:text-[#444444] px-2">Back</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main AIActionCenter ────────────────────────────────────────────────────────
/**
 * Props:
 *   leadId          string  — canonical lead _id
 *   lead            object  — full lead document (for prefill context)
 *   currentUser     object  — for capability checks
 *   onTaskCreated   fn()    — called after any task is created
 *   externalRefreshKey number — increment to force a recommendations reload (e.g. after marking actioned)
 *
 * Dispatch callbacks (open existing LeadDetail modals with prefill):
 *   onDispatchCall(prefill)         — opens LogCallModal
 *   onDispatchScheduleCall(prefill) — opens FollowUpModal (call not yet done)
 *   onDispatchMeeting(prefill)      — opens AddMeetingModal
 *   onDispatchFollowUp(prefill)     — opens FollowUpModal
 *   onDispatchNote(prefill)         — opens AddNoteModal
 *   onDispatchMessage(prefill)      — opens SendMessageModal
 */
export function AIActionCenter({
  leadId,
  lead,
  currentUser,
  onTaskCreated,
  externalRefreshKey = 0,
  onDispatchCall,
  onDispatchScheduleCall,
  onDispatchMeeting,
  onDispatchFollowUp,
  onDispatchNote,
  onDispatchMessage,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [edits, setEdits] = useState({});
  const [collapsed, setCollapsed] = useState(false);
  const [dispatching, setDispatching] = useState(null); // rec_id being dispatched

  // Guard against concurrent loads: track in-flight request count with a ref
  const _inFlight = useRef(0);

  const load = useCallback(async (useAi = false) => {
    // If a load is already in progress, skip to prevent race conditions
    if (_inFlight.current > 0) return;
    _inFlight.current += 1;
    setLoading(true);
    try {
      const res = await recommendationApi.getRecommendations(leadId, useAi);
      setData(res.data);
    } catch {
      // AI call failed — silently fall back to deterministic only
      try {
        const res2 = await recommendationApi.getRecommendations(leadId, false);
        setData(res2.data);
      } catch { setData(null); }
    } finally {
      _inFlight.current -= 1;
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  // External refresh — called by LeadDetail after successful persistence
  useEffect(() => {
    if (externalRefreshKey > 0) load(false);
  }, [externalRefreshKey]); // eslint-disable-line

  const handleToggle = (recId) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(recId) ? next.delete(recId) : next.add(recId);
      return next;
    });
  };

  const handleEdit = (recId, fields) => {
    setEdits(prev => ({ ...prev, [recId]: { ...(prev[recId] || {}), ...fields } }));
  };

  const handleDismiss = async (recId, action, snoozeHours = 48, reason = "") => {
    try {
      await recommendationApi.dismiss(leadId, recId, action, snoozeHours, reason);
      setData(prev => prev ? {
        ...prev,
        recommendations: prev.recommendations.filter(r => r.rec_id !== recId),
        recommendation_count: prev.recommendation_count - 1,
      } : prev);
      setSelected(prev => { const n = new Set(prev); n.delete(recId); return n; });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not apply dismiss action.";
      toast.error(detail);
    }
  };

  /**
   * Dispatch a non-TASK recommendation to the appropriate existing modal.
   * Sets dispatching state (spinner) then calls the appropriate prop callback.
   * The callback is responsible for opening the modal; after the modal confirms,
   * LeadDetail will call recommendationApi.markActioned and trigger an external refresh.
   */
  const handleDispatch = useCallback((rec, dispatchType) => {
    if (dispatching) return;
    setDispatching(rec.rec_id);

    // Build prefill context from the recommendation
    const prefill = {
      recId: rec.rec_id,
      title: rec.title,
      notes: rec.editable_description || rec.explanation || "",
      due_date: rec.suggested_due_date || "",
      priority: rec.priority || "MEDIUM",
      source: "AI_RECOMMENDATION",
      rec_rule_id: rec.rule_id,
    };

    // Immediately clear spinner — modal handles the rest
    setDispatching(null);

    switch (dispatchType) {
      case "LOG_CALL":
        if (onDispatchCall) {
          onDispatchCall(prefill);
        } else {
          toast.info("Use the Log Call button below to log this call");
        }
        break;
      case "SCHEDULE_CALL":
        if (onDispatchScheduleCall) {
          onDispatchScheduleCall({ ...prefill, follow_up_type: "MEETING_REQUESTED", reason: rec.title });
        } else {
          toast.info("Use the Follow-up button below to schedule this call");
        }
        break;
      case "SCHEDULE_MEETING":
        if (onDispatchMeeting) {
          onDispatchMeeting(prefill);
        } else {
          toast.info("Use the Add Meeting button below to schedule this meeting");
        }
        break;
      case "SCHEDULE_FOLLOW_UP":
        if (onDispatchFollowUp) {
          onDispatchFollowUp({ ...prefill, follow_up_type: "GENERAL", reason: rec.title });
        } else {
          toast.info("Use the Follow-up button below");
        }
        break;
      case "SAVE_NOTE":
        if (onDispatchNote) {
          onDispatchNote(prefill);
        } else {
          toast.info("Use the Add Note button below");
        }
        break;
      case "OPEN_COMPOSER":
        if (onDispatchMessage) {
          onDispatchMessage(prefill);
        } else {
          toast.info("Use the Send Message button below");
        }
        break;
      default:
        toast.info("Use the action buttons below to complete this recommendation");
    }
  }, [dispatching, onDispatchCall, onDispatchScheduleCall, onDispatchMeeting, onDispatchFollowUp, onDispatchNote, onDispatchMessage]);

  const canCreateActivities = hasCapability(currentUser, "crm.activity.create");

  const handleBatchCreate = async () => {
    if (!canCreateActivities) {
      toast.error("You do not have permission to create CRM tasks. Contact your CRM Manager.");
      return;
    }
    const taskRecs = (data?.recommendations || []).filter(
      r => r.action_type === "TASK" && selected.has(r.rec_id)
    );
    if (!taskRecs.length) return;

    setCreating(true);
    try {
      const items = taskRecs.map(r => {
        const e = edits[r.rec_id] || {};
        return {
          rec_id: r.rec_id,
          title: e.title || r.title,
          due_date: e.due_date || r.suggested_due_date,
          notes: e.description || r.editable_description,
          priority: e.priority || r.priority,
          user_edits: Object.keys(e).length > 0 ? e : null,
        };
      });

      const res = await recommendationApi.batchCreate(leadId, items);
      const created = res.data?.created?.length || 0;
      const skipped = res.data?.skipped?.length || 0;

      if (created > 0) {
        toast.success(`${created} task${created > 1 ? "s" : ""} created`);
        onTaskCreated?.();
      }
      if (skipped > 0) {
        toast.info(`${skipped} task${skipped > 1 ? "s" : ""} already existed — skipped`);
      }
      if (created === 0 && skipped === 0) {
        toast.warning("No tasks were created. Check task details and try again.");
      }

      setSelected(new Set());
      load(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error("You do not have permission to create CRM tasks.");
      } else if (status === 422) {
        toast.error("Task details are invalid. Check required fields and try again.");
      } else {
        toast.error("Failed to create tasks. Please try again.");
      }
    } finally {
      setCreating(false);
    }
  };

  const recs = data?.recommendations || [];
  const taskRecs = recs.filter(r => r.action_type === "TASK" && r.review_status === "PENDING_REVIEW");
  const selectedTaskCount = taskRecs.filter(r => selected.has(r.rec_id)).length;
  const pendingCount = data?.pending_count || 0;
  const hasAI = recs.some(r => r.ai_enhanced);

  if (loading) {
    return (
      <div className="border border-[#E5E5E5] rounded-xl p-4 space-y-2.5 animate-pulse" data-testid="ai-action-center-loading">
        <div className="h-4 bg-[#F3F3F3] rounded w-1/3" />
        <div className="h-16 bg-[#F3F3F3] rounded" />
        <div className="h-16 bg-[#F3F3F3] rounded" />
      </div>
    );
  }

  if (!data || recs.length === 0) {
    return (
      <div className="border border-[#E5E5E5] rounded-xl p-4 text-center" data-testid="ai-action-center-empty">
        <CheckCheck className="w-5 h-5 text-emerald-400 mx-auto mb-1.5" />
        <p className="text-sm text-[#706E6B]">No pending recommendations for this lead.</p>
        <button onClick={() => load()} className="mt-2 text-xs text-[#C9A15F] hover:underline flex items-center gap-1 mx-auto">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid="ai-action-center">
      {/* Panel header */}
      <div
        className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-[#1A1410]/5 to-transparent border-b border-[#E5E5E5] cursor-pointer select-none"
        onClick={() => setCollapsed(c => !c)}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#C9A15F]" />
          <span className="text-sm font-semibold text-[#1A1410] tracking-tight">Recommended Next Actions</span>
          {pendingCount > 0 && (
            <span className="text-[10px] font-bold bg-[#C9A15F] text-white rounded-full px-1.5 py-0.5">
              {pendingCount}
            </span>
          )}
          {hasAI ? (
            <span className="text-[10px] text-purple-500 font-medium flex items-center gap-0.5" data-testid="panel-ai-label">
              <Zap className="w-3 h-3" /> AI-assisted
            </span>
          ) : (
            <span className="text-[10px] text-slate-400 font-medium flex items-center gap-0.5" data-testid="panel-system-label">
              <CheckCircle2 className="w-3 h-3" /> System rules
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={e => { e.stopPropagation(); load(); }}
            className="text-[#706E6B] hover:text-[#444444] transition-colors p-1"
            title="Refresh recommendations"
            data-testid="ai-action-center-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          {collapsed ? <ChevronDown className="w-4 h-4 text-[#706E6B]" /> : <ChevronUp className="w-4 h-4 text-[#706E6B]" />}
        </div>
      </div>

      {!collapsed && (
        <div className="p-3 space-y-2.5">
          {/* Lead context row */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-[11px] text-[#706E6B] leading-relaxed max-w-lg">
              Suggested from event intent and current stage.{" "}
              <span className="font-medium text-[#444444]">Confirm customer details before taking action.</span>
            </p>
            {data?.intent_lifecycle && (
              <IntentLifecycleBadges lifecycle={data.intent_lifecycle} leadId={leadId} />
            )}
          </div>

          {/* Recommendation cards */}
          <div className="space-y-2" data-testid="rec-cards-list">
            {recs.map(rec => (
              <RecommendationCard
                key={rec.rec_id}
                rec={rec}
                selected={selected.has(rec.rec_id)}
                onToggle={handleToggle}
                onEdit={handleEdit}
                onDismiss={handleDismiss}
                canAct={canCreateActivities}
                dispatching={dispatching}
                onDispatch={handleDispatch}
              />
            ))}
          </div>

          {/* TASK batch create row */}
          {!canCreateActivities && (
            <div className="flex items-center gap-2 text-xs text-[#706E6B] pt-1 border-t border-[#F3F3F3]"
              data-testid="ai-batch-create-auth-note">
              <Lock className="w-3.5 h-3.5 text-[#B0AEAC] flex-shrink-0" />
              <span>Task creation requires CRM activity permissions. Contact your CRM Manager to create these tasks.</span>
            </div>
          )}
          {taskRecs.length > 0 && canCreateActivities && (
            <div className="flex items-center gap-3 pt-1 border-t border-[#F3F3F3]">
              <button
                onClick={handleBatchCreate}
                disabled={selectedTaskCount === 0 || creating}
                data-testid="ai-batch-create-btn"
                className={`flex items-center gap-1.5 text-sm px-4 py-1.5 rounded-lg font-medium transition-all ${
                  selectedTaskCount > 0 && !creating
                    ? "bg-[#C9A15F] text-white hover:bg-[#B8904E] shadow-sm"
                    : "bg-[#F3F3F3] text-[#B0AEAC] cursor-not-allowed"
                }`}
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />}
                {selectedTaskCount > 0
                  ? `Create ${selectedTaskCount} selected task${selectedTaskCount > 1 ? "s" : ""}`
                  : "Select tasks to create"}
              </button>
              <p className="text-[10px] text-[#706E6B]">
                Unselected drafts are not saved. Tasks appear in your activity feed below.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AIActionCenter;
